import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { Row } from 'src/components/atoms/Row';
import { AutoSuggest } from 'src/components/molecules/AutoSuggest';
import useOwnerList from 'src/features/owner/hooks/useOwnerList';
import { formatLabelValue } from 'src/helpers';
import { IValueLabel } from 'src/ts/interfaces';
import { PartialUser } from '../InvitationFlowPage';

type SelectOwnerFormProps = {
  onSave: (data: PartialUser) => void;
  onCancel: (data: PartialUser) => void;
  defaultValues: PartialUser;
  isDisabled?: boolean;
};

const EMPTY_VALUE = '';

const SelectOwnerForm: React.FC<SelectOwnerFormProps> = ({
  onSave,
  onCancel,
  defaultValues,
  isDisabled
}) => {
  const {
    onSearch: onSearchOwner,
    owners,
    isLoading: isLoadingOwners,
    error: ownersError
  } = useOwnerList();
  const methods = useForm({
    reValidateMode: 'onSubmit',
    defaultValues
  });

  const onFormSubmit = (data: PartialUser) => {
    onSave(data);
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onFormSubmit)}>
        <Controller
          name="owner.name"
          control={methods.control}
          rules={{ required: 'Owner is required' }}
          render={({ field: { name } }) => (
            <AutoSuggest
              name={name}
              data={
                formatLabelValue({
                  data: owners,
                  value: 'id',
                  label: 'name'
                }) as IValueLabel[]
              }
              placeholder="Owner"
              initialLabel={defaultValues.owner?.name || ''}
              onChange={({ value }) => {
                onSearchOwner(value.toString());
              }}
              onSelect={({ value: id, label: name }) => {
                methods.setValue('owner.id', id);
                methods.setValue('owner.name', name);
              }}
              onClear={() => {
                onSearchOwner(EMPTY_VALUE);
                methods.setValue('owner.id', EMPTY_VALUE);
                methods.setValue('owner.name', EMPTY_VALUE);
              }}
              isLoading={isLoadingOwners}
              hasError={ownersError !== null}
              isDisabled={isDisabled || ownersError !== null}
            />
          )}
        />

        <Gap height={1} />
        <Row gap={1}>
          <Button
            name="continue_button"
            variant={ButtonVariant.contained}
            color={ButtonColor.primary}
            size={ButtonSize.medium}
            type={ButtonType.submit}
            isDisabled={ownersError !== null}
          >
            Continue
          </Button>
          <Button
            name="cancel_button"
            variant={ButtonVariant.outline}
            color={ButtonColor.primary}
            size={ButtonSize.medium}
            type={ButtonType.button}
            onClick={() =>
              onCancel({
                ...defaultValues,
                owner: {
                  name: methods.getValues('owner.name') || '',
                  id: methods.getValues('owner.id') || ''
                }
              })
            }
          >
            Cancel
          </Button>
        </Row>
      </form>
    </FormProvider>
  );
};

export default SelectOwnerForm;
